import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { clearWrongCredentialsError, fetchToken } from '@app/store/slices/token';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import * as S from './LoginForm.styles';
import { getConfig } from '@app/services/configService';

interface LoginFormData {
  email: string;
  password: string;
}

export const initValues: LoginFormData = {
  email: '',
  password: '',
};

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);

  const { wrongCredentials } = useAppSelector((state) => state.token);
  const { currentCanton } = useAppSelector((state) => state.config);

  const clearWrongCredentialsIfShown = () => {
    if (wrongCredentials) {
      dispatch(clearWrongCredentialsError());
    }
  };

  const handleSubmit = (values: LoginFormData) => {
    setLoading(true);
    clearWrongCredentialsIfShown();
    dispatch(fetchToken(values))
      .unwrap()
      .then(() => navigate('/'))
      .catch((err) => {
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  useEffect(() => {
    return () => {
      clearWrongCredentialsIfShown();
    };
  }, []);

  const handleAnyInputChange = () => clearWrongCredentialsIfShown();

  const ssoEnabled = currentCanton?.adminToolSsoEnabled ?? true;
  const { apiBaseUrl, ssoLoginPath, ssoOnly } = getConfig();
  const baseUrl = apiBaseUrl || process.env.REACT_APP_API_BASE_URL || '';
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = ssoLoginPath.startsWith('/') ? ssoLoginPath : `/${ssoLoginPath}`;
  const ssoUrl = `${normalizedBaseUrl}${normalizedPath}`;

  // If ssoOnly is true, show only SSO button
  if (ssoOnly && ssoEnabled) {
    return (
      <Auth.FormWrapper>
        <BaseForm size="large" layout="vertical" requiredMark="optional">
          <Auth.FormTitle>{t('common.login')}</Auth.FormTitle>
          <S.SSOWrapper>
            <S.SSOButton type="button" onClick={() => (window.location.href = ssoUrl)}>
              {t('common.ssoLogin')}
            </S.SSOButton>
          </S.SSOWrapper>
        </BaseForm>
      </Auth.FormWrapper>
    );
  }

  return (
    <Auth.FormWrapper>
      <BaseForm
        size="large"
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
        initialValues={initValues}
      >
        <Auth.FormTitle>{t('common.login')}</Auth.FormTitle>
        <Auth.FormItem
          name="email"
          label={t('common.email')}
          rules={[
            { required: true, message: t('common.requiredField') },
            {
              type: 'email',
              message: t('common.notValidEmail'),
            },
          ]}
        >
          <Auth.FormInput placeholder={t('common.email')} onChange={() => handleAnyInputChange()} />
        </Auth.FormItem>
        <Auth.FormItem
          label={t('common.password')}
          name="password"
          rules={[{ required: true, message: t('common.requiredField') }]}
        >
          <Auth.FormInputPassword placeholder={t('common.password')} onChange={() => handleAnyInputChange()} />
        </Auth.FormItem>
        {wrongCredentials ? (
          <Auth.ErrorWrapper>
            <Auth.ErrorText>{t('forms.stepFormLabels.loginFailed')}</Auth.ErrorText>
          </Auth.ErrorWrapper>
        ) : (
          <></>
        )}
        <Auth.ActionsWrapper>
          <Link to="/auth/forgot-password">
            <S.ForgotPasswordText>{t('common.forgotPass')}</S.ForgotPasswordText>
          </Link>
        </Auth.ActionsWrapper>
        <BaseForm.Item noStyle>
          <Auth.SubmitButton type="primary" htmlType="submit" loading={isLoading}>
            {t('common.login')}
          </Auth.SubmitButton>
        </BaseForm.Item>
        {ssoEnabled && (
          <S.SSOWrapper>
            <S.SSOLabel>{t('common.orUseSSO')}</S.SSOLabel>
            <S.SSOButton type="button" onClick={() => (window.location.href = ssoUrl)}>
              {t('common.ssoLogin')}
            </S.SSOButton>
          </S.SSOWrapper>
        )}
      </BaseForm>
    </Auth.FormWrapper>
  );
};
